import { useEffect, useMemo, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import { addEvidence, captionEvidence, evidenceSrc, removeEvidence } from '@/lib/evidence';
import { ACCEPTED, FileTooLarge, UnsupportedFile, fileSize } from '@/lib/images';
import { dateTime } from '@/lib/format';
import { toast } from '@/stores/toastStore';
import { Button } from '@/components/ui/Button';
import { StatusChip } from '@/components/ui/StatusChip';
import type { Attachment, AttachmentEntity } from '@/types/models';

interface EvidenceProps {
  entityType: AttachmentEntity;
  /** Undefined while the parent record is still unsaved. */
  entityId: string | undefined;
  label?: string;
  help?: string;
  readOnly?: boolean;
  /** The record's site, when it is not the quarry (a branch's fuel fill). */
  siteId?: string;
}

/**
 * Photographic evidence against one record.
 *
 * Capture is deliberately one tap: `capture="environment"` opens the rear
 * camera straight from the drawer rather than sending a supervisor into the
 * gallery app and back. Files are downscaled before they are queued, and a
 * photograph appears here the instant it is taken whether or not there is a
 * signal — the badge says which ones are still waiting to go up.
 */
export function Evidence({
  entityType, entityId, label = 'Photographs', help, readOnly, siteId,
}: EvidenceProps) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState<Attachment | null>(null);

  const rows = useLiveQuery(async () => {
    if (!entityId) return [] as Attachment[];
    const all = await db.attachments
      .where('[entity_type+entity_id]')
      .equals([entityType, entityId])
      .toArray();
    return all
      .filter((a) => !a.deleted_at)
      .sort((a, b) => a.taken_at.localeCompare(b.taken_at));
  }, [entityType, entityId]);

  const pendingIds = useLiveQuery(
    async () => new Set((await db.evidence_blobs.toArray()).map((b) => b.id)),
    [],
  );

  async function onPick(files: FileList | null) {
    if (!files?.length || !entityId) return;
    setBusy(true);
    let added = 0;
    for (const file of Array.from(files)) {
      try {
        await addEvidence(entityType, entityId, file, undefined, siteId);
        added++;
      } catch (err) {
        toast.error(
          err instanceof FileTooLarge || err instanceof UnsupportedFile
            ? err.message
            : `Could not attach ${file.name}.`,
        );
      }
    }
    setBusy(false);
    if (input.current) input.current.value = '';
    if (added) toast.success(added === 1 ? 'Photograph attached' : `${added} photographs attached`);
  }

  if (!entityId) {
    return (
      <div className="evidence">
        <div className="form-section">{label}</div>
        <p className="small muted" style={{ marginTop: 0 }}>
          Save the record first — a photograph has to belong to something.
        </p>
      </div>
    );
  }

  const list = rows ?? [];

  return (
    <div className="evidence">
      <div className="form-section">
        {label}
        {list.length > 0 && <span className="muted"> · {list.length}</span>}
      </div>
      {help && <p className="small muted" style={{ marginTop: 0 }}>{help}</p>}

      {list.length === 0 && (
        <p className="small muted" style={{ marginTop: 0 }}>
          Nothing attached yet.
        </p>
      )}

      <div className="evidence__grid">
        {list.map((a) => (
          <EvidenceTile
            key={a.id}
            row={a}
            pending={pendingIds?.has(a.id) ?? false}
            onOpen={() => setViewing(a)}
          />
        ))}
      </div>

      {!readOnly && (
        <>
          <input
            ref={input}
            type="file"
            accept={ACCEPTED}
            capture="environment"
            multiple
            hidden
            onChange={(e) => void onPick(e.target.files)}
          />
          <Button
            variant="secondary"
            loading={busy}
            onClick={() => input.current?.click()}
          >
            + Add photograph
          </Button>
        </>
      )}

      {viewing && (
        <EvidenceViewer
          row={viewing}
          readOnly={readOnly}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

function EvidenceTile({
  row, pending, onOpen,
}: { row: Attachment; pending: boolean; onOpen: () => void }) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let revoke: string | null = null;
    let cancelled = false;
    void evidenceSrc(row).then((url) => {
      if (cancelled) {
        if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
        return;
      }
      if (url?.startsWith('blob:')) revoke = url;
      setSrc(url);
    });
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [row]);

  const isPdf = row.mime_type === 'application/pdf';

  return (
    <button className="evidence__tile" onClick={onOpen} type="button"
      aria-label={row.caption || row.file_name || 'Open photograph'}>
      {isPdf ? (
        <span className="evidence__pdf">PDF</span>
      ) : src ? (
        <img src={src} alt={row.caption ?? ''} loading="lazy" />
      ) : (
        <span className="evidence__missing">
          {navigator.onLine ? '…' : 'offline'}
        </span>
      )}
      {pending && <span className="evidence__badge">Waiting</span>}
    </button>
  );
}

function EvidenceViewer({
  row, readOnly, onClose,
}: { row: Attachment; readOnly?: boolean; onClose: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [caption, setCaption] = useState(row.caption ?? '');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let revoke: string | null = null;
    void evidenceSrc(row).then((url) => {
      if (url?.startsWith('blob:')) revoke = url;
      setSrc(url);
    });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [row]);

  const meta = useMemo(() => [
    dateTime(row.taken_at),
    row.width && row.height ? `${row.width}×${row.height}` : null,
    fileSize(row.bytes),
  ].filter(Boolean).join(' · '), [row]);

  return (
    <div className="lightbox" onMouseDown={onClose}>
      <div className="lightbox__inner" onMouseDown={(e) => e.stopPropagation()}>
        <header className="lightbox__bar">
          <span className="small">{meta}</span>
          <span className="spacer" />
          <button className="btn btn--ghost" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="lightbox__stage">
          {row.mime_type === 'application/pdf' ? (
            src
              ? <iframe src={src} title={row.file_name ?? 'Document'} />
              : <p className="muted">The document is not available offline.</p>
          ) : src ? (
            <img src={src} alt={row.caption ?? row.file_name ?? 'Evidence'} />
          ) : (
            <p className="muted">
              This photograph has synced, so the file is in the bucket rather
              than on this device — it needs a signal to open.
            </p>
          )}
        </div>

        <footer className="lightbox__bar">
          <input
            aria-label="Caption"
            placeholder="Caption — what does this show?"
            value={caption}
            disabled={readOnly}
            onChange={(e) => setCaption(e.target.value)}
            onBlur={() => {
              if ((row.caption ?? '') !== caption) void captionEvidence(row.id, caption);
            }}
          />
          {!readOnly && (
            <Button
              variant="danger"
              onClick={() => { void removeEvidence(row.id); onClose(); }}
            >
              Remove
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
}

/** The camera badge on a register row. */
export function EvidenceCount({ count }: { count: number }) {
  if (!count) return null;
  return <StatusChip tone="grey" dot={false}>▣ {count}</StatusChip>;
}
